import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, MessageCircle, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';

const DataDeletion = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Here you would typically send the request to your API
      // For now, we'll simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1500));

      setSubmitted(true);
      setEmail('');
      setReason('');
    } catch (err) {
      setError('Failed to submit deletion request. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white fill-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">TicketDesk</h1>
                <p className="text-xs text-gray-500">Customer Support Platform</p>
              </div>
            </Link>

            <Link
              to="/"
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Data Deletion Request</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We respect your right to have your data deleted. Use this page to request deletion of your personal information from TicketDesk.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12">

          {!submitted ? (
            <>
              {/* Information Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Request Data Deletion</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you would like to delete your personal data from TicketDesk, you have several options:
                </p>

                <div className="space-y-4">
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                    <h3 className="font-semibold text-blue-900 mb-2">Option 1: Delete Your Account (Recommended)</h3>
                    <p className="text-blue-800 text-sm leading-relaxed">
                      Log in to your TicketDesk account, go to Settings → Profile Settings → Account, and click "Delete Account". This will immediately remove all your data.
                    </p>
                  </div>

                  <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded">
                    <h3 className="font-semibold text-purple-900 mb-2">Option 2: Submit a Deletion Request</h3>
                    <p className="text-purple-800 text-sm leading-relaxed">
                      Fill out the form below to request data deletion. We will process your request within 30 days and send you a confirmation email.
                    </p>
                  </div>

                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <h3 className="font-semibold text-green-900 mb-2">Option 3: Contact Support</h3>
                    <p className="text-green-800 text-sm leading-relaxed">
                      Email us at <a href="mailto:privacy@ticketdesk.app" className="font-medium underline">privacy@ticketdesk.app</a> with your deletion request.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Submit Deletion Request Form</h3>

                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder="your@email.com"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Enter the email address associated with your TicketDesk account
                    </p>
                  </div>

                  <div>
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Deletion (Optional)
                    </label>
                    <textarea
                      id="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none"
                      placeholder="Please let us know why you're requesting data deletion (optional)"
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-yellow-900 mb-1">Important Notice</h4>
                        <p className="text-yellow-800 text-sm leading-relaxed">
                          Deleting your data is permanent and cannot be undone. This will remove:
                        </p>
                        <ul className="mt-2 text-yellow-800 text-sm space-y-1 ml-4 list-disc">
                          <li>Your account and profile information</li>
                          <li>All support tickets and conversations</li>
                          <li>Team collaboration data</li>
                          <li>Connected integrations</li>
                          <li>Usage history and analytics</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={loading || !email}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <Trash2 size={18} />
                          Submit Deletion Request
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Additional Information */}
              <div className="mt-8 border-t border-gray-200 pt-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">What Happens Next?</h3>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-purple-600 text-xs font-bold">1</span>
                    </div>
                    <p className="text-sm">We will verify your identity and account ownership</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-purple-600 text-xs font-bold">2</span>
                    </div>
                    <p className="text-sm">Your data will be permanently deleted from our systems within 30 days</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-purple-600 text-xs font-bold">3</span>
                    </div>
                    <p className="text-sm">You will receive a confirmation email once the deletion is complete</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-purple-600 text-xs font-bold">4</span>
                    </div>
                    <p className="text-sm">Some data may be retained for legal compliance or backup purposes for up to 90 days</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Success Message */
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Request Submitted Successfully</h2>
              <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
                We have received your data deletion request. You will receive a confirmation email shortly with the next steps. Your data will be permanently deleted within 30 days.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto mb-6">
                <p className="text-blue-900 font-medium mb-2">Need immediate assistance?</p>
                <p className="text-blue-800 text-sm">
                  Contact our privacy team at <a href="mailto:privacy@ticketdesk.app" className="font-medium underline">privacy@ticketdesk.app</a>
                </p>
              </div>
              <button
                onClick={() => setSubmitted(false)}
                className="text-purple-600 hover:text-purple-700 font-medium transition-colors"
              >
                Submit Another Request
              </button>
            </div>
          )}
        </div>

        {/* Legal Information */}
        <div className="mt-8 bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Legal Rights</h3>
          <p className="text-gray-700 text-sm leading-relaxed mb-3">
            Under GDPR (European Union) and CCPA (California), you have the right to request deletion of your personal data. TicketDesk complies with all applicable data protection regulations.
          </p>
          <p className="text-gray-700 text-sm leading-relaxed">
            For more information about how we handle your data, please review our <Link to="/privacy" className="text-purple-600 hover:text-purple-700 font-medium">Privacy Policy</Link>.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center mt-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Return to Homepage
          </Link>
        </div>
      </div>

      {/* Footer Section */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white fill-white" />
              </div>
              <span className="text-gray-600">© 2025 TicketDesk. All rights reserved.</span>
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-600 hover:text-purple-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-600 hover:text-purple-600 transition-colors">Terms of Service</Link>
              <Link to="/data-deletion" className="text-gray-600 hover:text-purple-600 transition-colors">Data Deletion</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DataDeletion;
